import { useNavigate } from 'react-router-dom';
import { Building2, Check, ChevronsUpDown } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '@reflex/ui/components/ui/dropdown-menu';
import { cn } from '@reflex/ui/lib/utils';
import type { AuthMembership } from '@/lib/api';
import { useAuth } from '@/hooks/auth/useAuth';
import { orgBasePath } from '@/lib/routes';

interface WizardOrgSwitcherProps {
  /**
   * Slug of the org currently being onboarded. In existing mode this is
   * the wizard's org slug, and the switcher hides when no OTHER orgs
   * exist. In new mode (`/org-setup/new`) leave this `null` — every
   * existing membership is then a valid escape hatch back to a
   * bootstrapped org, so the switcher renders whenever the user has at
   * least one org.
   */
  currentOrgSlug?: string | null;
  /**
   * Trigger label when there is no `currentOrgSlug`. Defaults to the
   * create-org wizard's subject; a screen that is not building an org
   * (the `/org-setup` decision screen) should say what it is instead.
   */
  placeholderLabel?: string;
}

type Org = AuthMembership['organization'];

/** Square org mark: the uploaded avatar, else the name's initials. */
function OrgMark({ org, className }: { org: Org; className?: string }) {
  if (org.avatarUrl) {
    return (
      <img
        src={org.avatarUrl}
        alt=""
        className={cn('shrink-0 rounded-sm object-cover', className)}
        aria-hidden="true"
      />
    );
  }
  return (
    <span
      className={cn(
        'flex shrink-0 items-center justify-center rounded-sm bg-muted text-[10px] font-semibold uppercase leading-none text-muted-foreground',
        className,
      )}
      aria-hidden="true"
    >
      {org.name.slice(0, 2)}
    </span>
  );
}

/**
 * Org switcher tailored for the `WizardLayout` brand row. Mirrors the
 * sidebar `OrgSwitcher` but is light-themed and only renders when the
 * signed-in user has somewhere else to go — the wizard otherwise traps
 * users inside one org's setup flow (or the create-new-org flow).
 *
 * Unlike the sidebar switcher (which leaves active-org sync to
 * `OrgSlugResolver` because it stays inside `AuthedShell`), this
 * component navigates from the wizard shell into `AuthedShell`, so
 * `activeOrganizationId` must be updated synchronously before the
 * route change — otherwise `OrgGate` re-evaluates with the stale
 * active org and bounces the user back to the previous org's setup.
 */
export function WizardOrgSwitcher({
  currentOrgSlug = null,
  placeholderLabel = 'New organization',
}: WizardOrgSwitcherProps = {}) {
  const { memberships, setActiveOrganizationId } = useAuth();
  const navigate = useNavigate();

  if (!memberships || memberships.length === 0) return null;

  // Switchable = orgs other than the one being onboarded. In new mode
  // (`currentOrgSlug == null`) the user isn't a member of the org under
  // construction yet, so every existing membership counts.
  const switchable = memberships.filter((m) => m.organization.slug !== currentOrgSlug);
  if (switchable.length === 0) return null;

  const currentOrg = currentOrgSlug
    ? (memberships.find((m) => m.organization.slug === currentOrgSlug)?.organization ?? null)
    : null;
  // In new mode we don't want to label the trigger with an existing
  // org's name (which would imply the user is "in" that org), so fall
  // back to a neutral label that matches the wizard's purpose.
  const triggerLabel = currentOrg?.name ?? placeholderLabel;

  const handleSelect = (orgId: string, slug: string) => {
    if (slug === currentOrgSlug) return;
    setActiveOrganizationId(orgId);
    navigate(`${orgBasePath(slug)}/`);
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="group flex max-w-[220px] items-center gap-2 rounded-md border border-border bg-background py-1 pl-1 pr-2 text-sm text-foreground transition-colors hover:bg-accent focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring/60 data-[state=open]:bg-accent"
          data-testid="wizard-org-switcher-trigger"
          aria-label="Switch organization"
        >
          {currentOrg ? (
            <OrgMark org={currentOrg} className="size-6" />
          ) : (
            <span
              className="flex size-6 shrink-0 items-center justify-center rounded-sm bg-muted text-muted-foreground"
              aria-hidden="true"
            >
              <Building2 className="size-3.5" />
            </span>
          )}
          <span className="min-w-0 flex-1 truncate text-left font-medium">{triggerLabel}</span>
          <ChevronsUpDown
            className="size-3.5 shrink-0 text-muted-foreground/60 transition-colors group-hover:text-muted-foreground"
            aria-hidden="true"
          />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-72" align="end" sideOffset={6}>
        <DropdownMenuLabel className="-mx-1 -mt-1 mb-1 border-b border-border bg-muted/60 px-2.5 py-1.5 text-2xs font-medium uppercase tracking-wider text-muted-foreground">
          Switch organization
        </DropdownMenuLabel>
        {/* Long membership lists stay usable inside the brand row instead of
            running off the bottom of the viewport. */}
        <div className="max-h-[min(60vh,20rem)] overflow-y-auto">
          {memberships.map(({ organization }) => {
            const isCurrent = organization.slug === currentOrgSlug;
            // Switching to an org that has never been bootstrapped drops the
            // user into *that* org's wizard, so label it rather than let the
            // escape hatch look like a dead end when it lands on another form.
            const needsSetup = organization.bootstrappedAt === null;
            return (
              <DropdownMenuItem
                key={organization.id}
                onSelect={() => handleSelect(organization.id, organization.slug)}
                className="gap-2.5 py-2"
                data-testid={`wizard-org-switcher-item-${organization.slug}`}
                data-active={isCurrent ? 'true' : undefined}
              >
                <OrgMark org={organization} className="size-7" />
                <span className="flex min-w-0 flex-1 flex-col">
                  <span className="truncate text-sm font-medium leading-tight">
                    {organization.name}
                  </span>
                  <span className="truncate text-xs leading-tight text-muted-foreground">
                    {isCurrent ? 'Setting up' : needsSetup ? 'Setup needed' : organization.slug}
                  </span>
                </span>
                {isCurrent && (
                  <Check className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
                )}
              </DropdownMenuItem>
            );
          })}
        </div>
        {/* Only true of a saved org's wizard. Nothing is persisted while the
            create-org form is being filled in, so promising it would be a
            lie on `/org-setup/new` and meaningless on the welcome screen. */}
        {currentOrg && (
          <p className="border-t border-border px-2.5 pb-1 pt-2 text-xs text-muted-foreground">
            Setup stays here. You can come back to it any time.
          </p>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
